import { Group, Modal, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { UserSelect } from 'components/selects/UserSelect';
import panic from 'errors/panic';
import { useCallback } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

export interface TableProfilesModalProps {
  userId: number;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for marking the device's fault as fixed.
 */
export default function TableProfilesModal({ userId, opened, onClose }: TableProfilesModalProps) {
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      sourceUserId: '',
    },
    validate: {
      sourceUserId: createValidator([required]),
    },
  });

  const submit = useCallback(
    ({ sourceUserId }: typeof form.values) => {
      startLoading();

      const copyTableProfiles = getAction('UserCopyTableProfiles');

      copyTableProfiles({ payload: { sourceUserId: Number(sourceUserId), targetUserId: userId } })
        .then(() => {
          onClose();
          showNotification({
            title: 'Profily boli úspešne skopírované',
            message: '',
            color: SUCCESS_NOTIFICATION_COLOR,
          });
        })
        .catch(panic)
        .finally(stopLoading);
    },
    [onClose]
  );

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title="Tabuľkové profily"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={24}>
          <UserSelect
            label="Kopírovať od používateľa"
            placeholder="Vyberte používateľa"
            readOnly={loading}
            showDiscarded={false}
            {...form.getInputProps('sourceUserId')}
          />

          <Group position="right" spacing={16} display={loading ? 'none' : undefined}>
            <Button variant="subtle-gray" onClick={onClose} px={8}>
              Zrušiť
            </Button>
            <Button type="submit" variant="primary">
              Kopírovať
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
